import type { Detail } from "./adrTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { AdrLink } from "./AdrLink";
import { useApiResource } from "./useApiResource";

// ADRReader is the spec §6.1 ADR reader: rendered content with a sticky
// TOC, context/decision/consequences sections (surfaced naturally by the
// TOC over the document's own H2 structure, not re-derived separately),
// amendments, related decisions, referencing capabilities, related design
// documents, and a source link.
//
// The server sanitises `html` (internal/markdown — goldmark with no raw
// HTML passthrough + a bluemonday allow-list; see CLAUDE.md for the full
// trust-boundary writeup) before it ever reaches this component, so setting
// it via dangerouslySetInnerHTML here is safe — this is the one place in
// the SPA that does so, and it must stay that way: never point this at
// unsanitised content.
export function ADRReader({ token, id }: { token: string; id: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Detail>(token, repo, `/adrs/${encodeURIComponent(id)}`);

  const backLink = (
    <a
      href={repoPath(repo, "adrs")}
      onClick={(e) => {
        e.preventDefault();
        navigate(repoPath(repo, "adrs"));
      }}
    >
      ← Back to ADR library
    </a>
  );

  if (status === "loading") {
    return (
      <section className="panel">
        {backLink}
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready" || !data) {
    return (
      <section className="panel">
        {backLink}
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const toc = data.toc ?? [];
  const amendments = data.amendments ?? [];
  const relatedDecisions = data.relatedDecisions ?? [];
  const referencingCapabilities = data.referencingCapabilities ?? [];
  const relatedDesigns = data.relatedDesigns ?? [];

  return (
    <div className="doc-reader">
      <section className="panel doc-content">
        {backLink}
        <h2>{data.title}</h2>
        <p className="mono muted-note">
          {data.id} · {data.status}
        </p>
        {/* eslint-disable-next-line react/no-danger -- server-sanitised, see component docstring */}
        <div className="rendered-markdown" dangerouslySetInnerHTML={{ __html: data.html }} />

        <h3>Amendments</h3>
        {amendments.length > 0 ? (
          <ul>
            {amendments.map((a, i) => (
              <li key={i}>
                <span className="mono muted-note">{a.date}</span> {a.heading || a.text}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None recorded.</p>
        )}

        <h3>Related decisions</h3>
        {relatedDecisions.length > 0 ? (
          <ul className="adr-list">
            {relatedDecisions.map((d) => (
              <li key={d.id}>
                <AdrLink id={d.id} title={d.title} />
                <span>{d.title || <span className="muted-note">(unresolved reference)</span>}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None declared.</p>
        )}

        <h3>Referencing capabilities</h3>
        {referencingCapabilities.length > 0 ? (
          <ul>
            {referencingCapabilities.map((c) => (
              <li key={c.id}>
                <span className="mono">{c.id}</span> {c.name}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">No capability cites this ADR.</p>
        )}

        <h3>Related designs</h3>
        {relatedDesigns.length > 0 ? (
          <ul>
            {relatedDesigns.map((d) => (
              <li key={d.sourcePath}>{d.title}</li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None found.</p>
        )}

        <p>
          <a href={data.sourceUrl} target="_blank" rel="noopener noreferrer">
            View source on GitHub
          </a>
        </p>
      </section>

      {toc.length > 0 && (
        <nav className="doc-toc" aria-label="Table of contents">
          <h3>Contents</h3>
          <ul>
            {toc.map((h) => (
              <li key={h.id} className={h.level === 3 ? "toc-sub" : undefined}>
                <a href={`#${h.id}`}>{h.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
